import { Component, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CampaignFilterComponent, CampaignPerformanceRequest } from '../../../shared/components/campaign-filter/campaign-filter.component';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { LineAreaChartComponent } from '../../../shared/components/line-area-chart/line-area-chart.component';
import { FilterOptions } from '../../../shared/components/data-filters/data-filters.component';
import { CampaignPerformance, CampaignPerformanceRate } from '../../../shared/services/campaign.service';
import { CampaignPerformanceService } from './campaign-performance.service';
import { CampaignAnalyticsService } from '../campaign-analytics/campaign-analytics.service';
import { ChartSeries } from '../../../shared/models/chart.model';
import { ChartColors } from '../../../core/config/chart.config';
import { ChartLegends } from '../../../shared/models/chart-legends';
import { EmailMetrics, EmailEffectivenessMetrics, EmailReadershipRate, EmailRejectionRate, CampaignFilterOptions, CampaignSummary } from '../campaign-analytics.model';
import { forkJoin } from 'rxjs';

// Add this at the top of the file, outside the class
const ALL_CAMPAIGNS_CODE = 'All Campaigns';

@Component({
  selector: 'app-campaign-performance',
  standalone: true,
  imports: [
    CommonModule,
    CampaignFilterComponent,
    LoaderComponent,
    LineAreaChartComponent
  ],
  templateUrl: './campaign-performance.component.html',
  styleUrls: ['./campaign-performance.component.scss']
})
export class CampaignPerformanceComponent {
  // Loading state
  // loading: boolean = false; 

  loadingStates: { [key: string]: boolean } = {
    summary: false,
    performance: false,
    performanceRate: false,
    emailMetrics: false,
    emailEffectiveness: false,
    emailReadershipRate: false,
    emailRejectionRate: false
  };
  
  // summaryLoading: boolean = true; 
  
  // Chart loading states - using the new loadingStates system
  // chartLoadingStates = {
  //   performance: false,
  //   performanceRate: false,
  //   emailMetrics: false,
  //   emailEffectiveness: false,
  //   emailReadershipRate: false,
  //   emailRejectionRate: false
  // };
  
  // Campaign data
  selectedCampaign: string | null = null;

  // Chart data
  performanceData: any[] = [];
  performanceLegendInfo = ChartLegends.performance;
  
  // Performance rates data
  performanceRateData: any[] = [];
  performanceRateLegendInfo = ChartLegends.performanceRate;
  
  // New chart data from analytics
  emailMetricsData: EmailMetrics[] = [];
  emailEffectivenessData: EmailEffectivenessMetrics[] = [];
  emailReadershipRateData: EmailReadershipRate[] = [];
  emailRejectionRateData: EmailRejectionRate[] = [];
  
  // Campaign summary
  campaignSummary: CampaignSummary = {
    campaignCount: 0,
    emailsSent: 0,
    avgOpenRate: 0,
    avgCTR: 0,
    avgCTOR: 0,
    totalUnSubscribes: 0,
    totalBounced: 0
  };
  
  // Series configurations for performance metrics
  performanceSeries: ChartSeries[] = [
    {
      key: 'sent',
      name: 'Sent',
      color: ChartColors.primary
    },
    {
      key: 'delivered',
      name: 'Delivered',
      color: ChartColors.secondary
    },
    {
      key: 'opened',
      name: 'Opened',
      color: ChartColors.tertiary
    },
    {
      key: 'clicked',
      name: 'Clicked',
      color: ChartColors.quaternary
    },
    {
      key: 'bounced',
      name: 'Bounced',
      color: ChartColors.senary
    },
    {
      key: 'unsubscribes',
      name: 'Unsubscribes',
      color: ChartColors.quinary
    }
  ];
  
  // Series configurations for performance rate metrics
  performanceRateSeries: ChartSeries[] = [
    {
      key: 'openRate',
      name: 'Open Rate',
      color: ChartColors.primary
    },
    {
      key: 'ctor',
      name: 'CTOR',
      color: ChartColors.secondary
    },
    {
      key: 'ctr',
      name: 'CTR',
      color: ChartColors.tertiary
    },
    {
      key: 'bounceRate',
      name: 'Bounce Rate',
      color: ChartColors.quaternary
    },
    {
      key: 'unsubscribeRate',
      name: 'Unsubscribe Rate',
      color: ChartColors.quinary
    }
  ];
  
  // New series configurations from analytics
  emailMetricsSeries: ChartSeries[] = [
    {
      key: 'totalSends',
      name: 'Sent',
      color: ChartColors.primary
    },
    {
      key: 'totalDelivered',
      name: 'Delivered',
      color: ChartColors.secondary
    },
    {
      key: 'totalOpens',
      name: 'Unique Opened',
      color: ChartColors.tertiary
    },
    {
      key: 'totalClicks',
      name: 'Unique Clicked',
      color: ChartColors.quaternary
    }
  ];
  
  emailEffectivenessSeries: ChartSeries[] = [
    {
      key: 'totalSent',
      name: 'Sent',
      color: ChartColors.primary
    },
    {
      key: 'emailsDelivered',
      name: 'Delivered',
      color: ChartColors.secondary
    },
    {
      key: 'totalOpens',
      name: 'Unique Opened',
      color: ChartColors.tertiary
    },
    {
      key: 'totalClicks',
      name: 'Unique Clicked',
      color: ChartColors.quaternary
    }    
  ];
  
  emailReadershipRateSeries: ChartSeries[] = [
    {
      key: 'openRate',
      name: 'Unique Open Rate (%)',
      color: ChartColors.primary
    },
    {
      key: 'ctor',
      name: 'Unique CTOR',
      color: ChartColors.secondary
    },
    {
      key: 'ctr',
      name: 'Unique CTR',
      color: ChartColors.tertiary
    }
  ];
  
  emailRejectionRateSeries: ChartSeries[] = [
    {
      key: 'bounceRate',
      name: 'Bounce Rate',
      color: ChartColors.primary
    },
    {
      key: 'unsubscribeRate',
      name: 'Unsubscribe Rate',
      color: ChartColors.secondary
    }
  ];
  
  // Legend info for charts
  emailMetricsLegendInfo = ChartLegends.emailMetrics;
  emailEffectivenessLegendInfo = ChartLegends.emailEffectiveness;
  emailReadershipRateLegendInfo = ChartLegends.emailReadershipRate;
  emailRejectionRateLegendInfo = ChartLegends.emailRejectionRate;
  
  // Legend info for the charts
  seriesInfo: any[] = [];
  rateSeriesInfo: any[] = [];
  
  // Set default filters to January of current year and current month
  currentFilters: FilterOptions = {
    fromDate: this.getDateInYYYYMM(new Date(new Date().getFullYear(), 0, 1)), // January of current year
    toDate: this.getDateInYYYYMM(new Date()), // Current month
    journalId: '',
    regionId: '',
    institutionId: ''
  };
  
  constructor(
    private campaignPerformanceService: CampaignPerformanceService,
    private campaignAnalyticsService: CampaignAnalyticsService,
    private cdr: ChangeDetectorRef
  ) {}
  
  // Helper methods for loading states
  hasAnyLoadingState(): boolean {
    return Object.values(this.loadingStates).some(state => state);
  }

  setLoadingState(key: string, loading: boolean) {
    this.loadingStates[key] = loading;
  }

  resetAllLoadingStates() {
    Object.keys(this.loadingStates).forEach(key => {
      this.loadingStates[key] = false;
    });
  }
  
  onFiltersChanged(filters: FilterOptions): void {
    this.currentFilters = filters;
    this.loadAnalyticsData(this.selectedCampaign || ALL_CAMPAIGNS_CODE, filters);
  }
  
  // Handle campaign selection
  onCampaignSelected(performanceData: CampaignPerformanceRequest): void {
    this.loadingStates['performance'] = true;
    this.resetData();
    this.setAllLoadingStates(true);
    this.selectedCampaign = performanceData.campaignCode;
    
    // Update current filters with the new date range
    this.currentFilters = {
      ...this.currentFilters,
      fromDate: performanceData.fromDate,
      toDate: performanceData.toDate
    };
    
    // Only load performance data if a specific campaign is selected (not "All Campaigns")
    if (performanceData.campaignCode !== ALL_CAMPAIGNS_CODE) {
      // Use forkJoin to fetch both sets of data simultaneously
      forkJoin({
        performance: this.campaignPerformanceService.getCampaignPerformance(performanceData.campaignCode),
        performanceRate: this.campaignPerformanceService.getCampaignPerformanceRate(performanceData.campaignCode)
      }).subscribe(
        (result) => {
          if (result.performance) {
            this.prepareChartData(result.performance);
          }
          
          if (result.performanceRate) {
            this.prepareRateChartData(result.performanceRate);
          }
          
          this.loadingStates['performance'] = false;
          this.loadingStates['performanceRate'] = false;
          this.checkAllLoaded();
        },
        (error) => {
          console.error('Error loading campaign performance data:', error);
          this.loadingStates['performance'] = false;
          this.loadingStates['performanceRate'] = false;
          this.checkAllLoaded();
        }
      );
    } else {
      // For "All Campaigns", skip the individual performance charts
      this.loadingStates['performance'] = false;
      this.loadingStates['performanceRate'] = false;
      this.performanceData = [];
      this.performanceRateData = [];
    }
    
    // Load analytics data with campaign filter and current date filters
    this.loadAnalyticsData(performanceData.campaignCode, this.currentFilters);
  }
  
  // Helper method to format date as YYYYMM
  private getDateInYYYYMM(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${year}${month}`;
  }
  
  // Helper method to convert various date formats to YYYYMM
  private convertToYYYYMM(dateInput: string): string {
    let date: Date;
    
    // If it's already in YYYYMM format, return as is
    if (/^\d{6}$/.test(dateInput)) {
      return dateInput;
    }
    
    // Otherwise, parse as date and convert
    date = new Date(dateInput);
    return this.getDateInYYYYMM(date);
  }
  
  private loadAnalyticsData(campaignName: string | null, filters?: FilterOptions | null): void {
    if (!campaignName) return;
    
    this.setAnalyticsLoadingStates(true);
    
    // Pass empty string if "All Campaigns" is selected, otherwise pass the campaign name
    const campaignParam = campaignName === ALL_CAMPAIGNS_CODE ? '' : campaignName;
    
    // Use current filters or default to January of current year and current month
    const effectiveFilters = filters || this.currentFilters;
    
    const processedFilters: CampaignFilterOptions = {
      fromDate: effectiveFilters?.fromDate || this.getDateInYYYYMM(new Date(new Date().getFullYear(), 0, 1)),
      toDate: effectiveFilters?.toDate || this.getDateInYYYYMM(new Date()),
      campaignName: campaignParam
    };
    
    console.log('Processed filters:', processedFilters);
    
    // Load email metrics data
    this.campaignAnalyticsService.getEmailMetrics(processedFilters).subscribe(
      (data: EmailMetrics[]) => {
        this.handleChartDataLoaded('emailMetrics', data);
      },
      error => {
        this.handleChartError('emailMetrics', error);
      }
    );
    
    // Load email effectiveness data
    this.campaignAnalyticsService.getEmailEffectivenessMetrics(processedFilters).subscribe(
      (data: EmailEffectivenessMetrics[]) => {
        this.handleChartDataLoaded('emailEffectiveness', data);
      },
      error => {
        this.handleChartError('emailEffectiveness', error);
      }
    );
    
    // Load email readership rate data
    this.campaignAnalyticsService.getEmailReadershipRate(processedFilters).subscribe(
      (data: EmailReadershipRate[]) => {
        this.handleChartDataLoaded('emailReadershipRate', data);
      },
      error => {
        this.handleChartError('emailReadershipRate', error);
      }
    );
    
    // Load email rejection rate data
    this.campaignAnalyticsService.getEmailRejectionRate(processedFilters).subscribe(
      (data: EmailRejectionRate[]) => {
        this.handleChartDataLoaded('emailRejectionRate', data);
      },
      error => {
        this.handleChartError('emailRejectionRate', error);
      }
    );
    
    // Load campaign summary data
    this.campaignAnalyticsService.getCampaignSummary(processedFilters).subscribe({
      next: (data: CampaignSummary) => {
        this.campaignSummary = data;
        this.loadingStates['summary'] = false;
        this.checkAllLoaded();
      },
      error: (error: any) => {
        console.error('Error loading campaign summary:', error);
        this.loadingStates['summary'] = false;
        this.checkAllLoaded();
      }
    });
  }
  
  private resetData(): void {
    this.performanceData = [];
    this.performanceRateData = [];
    this.emailMetricsData = [];
    this.emailEffectivenessData = [];
    this.emailReadershipRateData = [];
    this.emailRejectionRateData = [];
    this.seriesInfo = [];
    this.rateSeriesInfo = [];
    this.loadingStates['summary'] = true;
  }
  
  private setAllLoadingStates(loading: boolean): void {
    Object.keys(this.loadingStates).forEach(key => {
      this.loadingStates[key] = loading;
    });
  }
  
  private setAnalyticsLoadingStates(loading: boolean): void {
    this.loadingStates['emailMetrics'] = loading;
    this.loadingStates['emailEffectiveness'] = loading;
    this.loadingStates['emailReadershipRate'] = loading;
    this.loadingStates['emailRejectionRate'] = loading;
  }
  
  private handleChartDataLoaded(chartType: string, data: any[]): void {
    switch (chartType) {
      case 'emailMetrics':
        this.emailMetricsData = data;
        break;
      case 'emailEffectiveness':
        this.emailEffectivenessData = data;
        break;
      case 'emailReadershipRate':
        this.emailReadershipRateData = data;
        break;
      case 'emailRejectionRate':
        this.emailRejectionRateData = data;
        break;
    }
    
    this.loadingStates[chartType] = false;
    this.checkAllLoaded();
  }
  
  private handleChartError(chartType: string, error: any): void {
    console.error(`Error loading ${chartType} data:`, error);
    this.loadingStates[chartType] = false;
    this.checkAllLoaded();
  }
  
  private checkAllLoaded(): void {
    const allLoaded = Object.values(this.loadingStates).every(state => !state);
    if (allLoaded && this.loadingStates['performance']) {
      this.loadingStates['performance'] = false;
      this.cdr.detectChanges();
    }
  }
  
  // Prepare chart data for the performance metrics
  private prepareChartData(data: CampaignPerformance): void {
    // Check if we have valid data
    if (!data || !data.campaignName) {
      console.warn('Invalid campaign performance data:', data);
      return;
    }
    
    // Create a single data point with all metrics
    const chartData = {
      campaign: data.campaignName,
      sent: data.sent || 0,
      delivered: data.delivered || 0,
      opened: data.opened || 0,
      clicked: data.clicked || 0,
      bounced: data.bounced || 0,
      unsubscribes: data.unsubscribes || 0
    };
    
    // Set the performance data as an array with a single item
    this.performanceData = [chartData];
    
    // Update legend info
    this.seriesInfo = this.performanceSeries.map(series => ({
      name: series.name,
      color: series.color,
      value: data[series.key as keyof CampaignPerformance] || 0
    }));
  }
  
  // Prepare chart data for the performance rate metrics
  private prepareRateChartData(data: CampaignPerformanceRate): void {
    // Check if we have valid data
    if (!data || !data.campaignName) {
      console.warn('Invalid campaign performance rate data:', data);
      return;
    }
    
    // Create a single data point with all rate metrics
    const chartData = {
      campaign: data.campaignName,
      openRate: data.openRate || 0,
      ctor: data.ctor || 0,
      ctr: data.ctr || 0,
      unsubscribeRate: data.unsubscribeRate || 0,
      bounceRate: data.bounceRate || 0
    };
    
    // Set the performance rate data as an array with a single item
    this.performanceRateData = [chartData];
    
    // Update legend info for rates - don't multiply by 100 as the API already provides percentages
    this.rateSeriesInfo = this.performanceRateSeries.map(series => {
      const value = data[series.key as keyof CampaignPerformanceRate];
      return {
        name: series.name,
        color: series.color,
        value: typeof value === 'number' ? value : 0, // Don't multiply by 100
        isPercentage: true
      };
    });
  }
} 